import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import './SuperAdminDashboard.css';

const SuperAdminDashboard = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [stats, setStats] = useState({
    totalTeachers: 0,
    totalEvaluations: 0,
    subjectsCount: 0,
    recentEvaluations: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedClassLevel, setSelectedClassLevel] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);
  const [selectedEvaluations, setSelectedEvaluations] = useState([]); 
  const [newPassword, setNewPassword] = useState('');
  const navigate = useNavigate();

  const checkApiConnection = async () => {
    try {
      // Check if the API server is accessible first
      await axios.get('/');
      return true;
    } catch (error) {
      toast.error('Cannot connect to the API server. Please ensure the backend is running.');
      return false;
    }
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminInfo');
    localStorage.removeItem('userRole');
    toast.success('Logged out successfully');
    navigate('/teacher/login');
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const statsRes = await axios.get('/api/admin/dashboard-stats', { headers });
        if (statsRes.data.success) setStats(statsRes.data.data);
      } catch (statsErr) {
        console.error('Stats fetch error:', statsErr.response?.data || statsErr.message);
        toast.error('Failed to load dashboard statistics');
      }
      try {
        const teachersRes = await axios.get('/api/admin/teachers', { headers });
        if (teachersRes.data.success) {
          // Map the received data to ensure consistent field names
          const mappedTeachers = teachersRes.data.data.map(teacher => ({
            id: teacher.id,
            teacher_name: teacher.teacher_name || teacher.name,
            name: teacher.teacher_name || teacher.name,
            email: teacher.email,
            mobile_number: teacher.mobile_number || teacher.phone,
            phone: teacher.mobile_number || teacher.phone,
            specialization: Array.isArray(teacher.specialization) ? teacher.specialization : 
                             (teacher.specialization ? [teacher.specialization] : []),
            subject: Array.isArray(teacher.specialization) ? teacher.specialization[0] : teacher.specialization,
            state: teacher.state || teacher.school,
            school: teacher.state || teacher.school,
            teaching_experience: teacher.teaching_experience || teacher.experience_years || 0,
            experience_years: teacher.teaching_experience || teacher.experience_years || 0,
            is_active: teacher.is_active !== undefined ? teacher.is_active : true,
            created_at: teacher.created_at,
            updated_at: teacher.updated_at
          }));
          setTeachers(mappedTeachers);
        }
      } catch (teachersErr) {
        console.error('Teachers fetch error:', teachersErr.response?.data || teachersErr.message);
        toast.error('Failed to load teachers data');
      }
      try {
        const evaluationsRes = await axios.get('/api/admin/evaluations', { headers });
        if (evaluationsRes.data.success) setEvaluations(evaluationsRes.data.data);
      } catch (evalsErr) {
        console.error('Evaluations fetch error:', evalsErr.response?.data || evalsErr.message);
        toast.error('Failed to load evaluations data');
      }
    } catch (error) {
      console.error('General fetch error:', error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/teacher/login'); 
      return;
    }

    // Check API connection first then fetch data
    checkApiConnection().then(isConnected => {
      if (isConnected) {
        fetchData();
      }
    });
  }, [navigate, fetchData]);



  const handleTeacherAction = async (teacherId, action) => {
    setSelectedTeacherId(teacherId);
    setActionType(action);
    setShowConfirmModal(true);
    if (action === 'change-password') setNewPassword('');
  };

  const confirmAction = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      if (actionType === 'delete') {
        if (selectedEvaluationId) {
          // Single delete
          await axios.delete(`/api/evaluations/${selectedEvaluationId}`, { headers });
          toast.success('Evaluation deleted successfully');
        } else if (selectedEvaluations.length > 0) {
          // Bulk delete
          await Promise.all(selectedEvaluations.map(id => axios.delete(`/api/evaluations/${id}`, { headers })));
          toast.success('Selected evaluations deleted');
        } else {
          // Delete teacher
          await axios.delete(`/api/admin/teachers/${selectedTeacherId}`, { headers });
          toast.success('Teacher deleted successfully');
        }
      } else if (actionType === 'disable' || actionType === 'enable') {
        const is_active = actionType === 'enable';
        await axios.put(`/api/admin/teachers/${selectedTeacherId}/status`, 
          { is_active }, 
          { headers }
        );
        toast.success(`Teacher ${actionType}d successfully`);
      }
      // Refresh data
      fetchData();
      setShowConfirmModal(false);
      setSelectedEvaluationId(null);
      setSelectedEvaluations([]);
    } catch (error) {
      if (selectedEvaluationId || selectedEvaluations.length > 0) {
        toast.error('Failed to delete evaluation(s)');
      } else {
        toast.error(`Failed to ${actionType} teacher`);
      }
    }
  };

  // Filter functions
  const filteredEvaluations = evaluations.filter(evaluation => {
    const subjectMatch = !selectedSubject || evaluation.subject === selectedSubject;
    const teacherMatch = !selectedTeacher || evaluation.teacher_id?.toString() === selectedTeacher;
    const classLevelMatch = !selectedClassLevel || evaluation.class_level === selectedClassLevel;
    return subjectMatch && teacherMatch && classLevelMatch;
  });

  const filteredTeachers = teachers.filter(teacher => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      teacher.teacher_name?.toLowerCase().includes(searchLower) ||
      teacher.email?.toLowerCase().includes(searchLower) ||
      teacher.specialization?.some(spec => spec.toLowerCase().includes(searchLower))
    );
  });

  // Get subjects from both evaluations and teacher specializations
  const evaluationSubjects = [...new Set(evaluations.map(e => e.subject).filter(Boolean))];
  const teacherSubjects = [...new Set(teachers.flatMap(t => 
    Array.isArray(t.specialization) ? t.specialization : (t.specialization ? [t.specialization] : [])
  ).filter(Boolean))];
  const subjects = [...new Set([...evaluationSubjects, ...teacherSubjects])].sort();

  // Get unique class levels from evaluations
  const classLevels = [...new Set(evaluations.map(e => e.class_level).filter(Boolean))].sort((a, b) => {
    // Try to sort numerically if possible, otherwise lexicographically
    const numA = parseInt(a);
    const numB = parseInt(b);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });

  if (loading) {
    return (
      <div className="admin-dashboard loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const renderOverviewTab = () => (
    <div className="overview-content">
      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card teachers">
          <div className="stat-icon">
            <i className="icon-users">👥</i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalTeachers}</h3>
            <p>Active Teachers</p>
            <span className="stat-change">Active users</span>
          </div>
        </div>
        <div className="stat-card evaluations">
          <div className="stat-icon">
            <i className="icon-evaluations">📝</i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalEvaluations}</h3>
            <p>Total Evaluations</p>
            <span className="stat-change">+{stats.recentEvaluations.length} recent</span>
          </div>
        </div>
        <div className="stat-card subjects">
          <div className="stat-icon">
            <i className="icon-subjects">📚</i>
          </div>
          <div className="stat-content">
            <h3>{stats.subjectsCount}</h3>
            <p>Total Subjects</p>
            <span className="stat-change">Across platform</span>
          </div>
        </div>
      </div>
      {/* Recent Activity */}
      <div className="recent-activity-section">
        <div className="section-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span style={{ fontSize: '1.2em' }}>📊</span>
            <h3 style={{ margin: 0, flex: '0 0 auto' }}>Recent Evaluations</h3>
            <div style={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
              <div className="score-metric-legend">
                <span className="score-badge excellent">Excellent (≥32)</span>
                <span className="score-badge good">Good (20–31)</span>
                <span className="score-badge needs-improvement">Needs Improvement (&lt;20)</span>
              </div>
            </div>
            <button 
              className="view-all-btn"
              onClick={() => setActiveTab('evaluations')}
              style={{ flex: '0 0 auto' }}
            >
              View All
            </button>
          </div>
        </div>
        <div className="activity-grid activity-grid-vertical">
          {stats.recentEvaluations.slice(0, 6).map((evaluation) => {
            const totalMarks = typeof evaluation.total_marks === 'number' ? evaluation.total_marks : (typeof evaluation.score === 'number' ? evaluation.score : 0);
            return (
              <div
                key={evaluation.id}
                className="activity-card activity-card-flex"
                style={{
                  background: '#F8FAFC',
                  padding: '10px 12px',
                  fontSize: '0.98em',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  alignItems: 'center',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                }}
              >
                {/* Student Name */}
                <div className="activity-detail activity-detail-name" style={{ minWidth: 0, flex: '1 1 120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <span className="activity-label">Name:</span> <span className="activity-value">{evaluation.student_name}</span>
                </div>
                {/* Teacher Name */}
                <div className="activity-detail activity-detail-teacher" style={{ minWidth: 0, flex: '1 1 120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <span className="activity-label">Teacher:</span> <span className="activity-value">{evaluation.teacher_name || 'Unknown Teacher'}</span>
                </div>
                {/* Class */}
                <div className="activity-detail activity-detail-class" style={{ minWidth: 0, flex: '1 1 120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.97em' }}>
                  <span className="activity-label">Class:</span>
                  <span className="activity-value activity-value-class">{evaluation.class_level || 'N/A'}</span>
                </div>
                {/* Subject */}
                <div className="activity-detail activity-detail-subject" style={{ minWidth: 0, flex: '1 1 100px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.97em' }}>
                  <span className="activity-label">Subject:</span>
                  <span className="activity-value subject-badge">{(evaluation.subject || '').toUpperCase()}</span>
                </div>
                {/* Date */}
                <div className="activity-detail activity-detail-date" style={{ minWidth: 0, flex: '1 1 100px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.97em' }}>
                  <span className="activity-label">Date:</span> <span className="activity-value">{new Date(evaluation.created_at).toLocaleDateString()}</span>
                </div>
                {/* Score */}
                <div className="activity-detail activity-detail-score" style={{ flex: '0 0 auto', fontSize: '0.97em' }}>
                  <div className={`score-badge ${
                    totalMarks >= 32 ? 'excellent' : totalMarks >= 20 ? 'good' : 'needs-improvement'
                  }`}>
                    <span className="activity-label">Score:</span> <span className="activity-value">{totalMarks ? `${totalMarks}/40` : '/40'}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderTeachersTab = () => (      <div className="teachers-content">
      <div className="section-header">
        <h3>Teacher Management</h3>
        <div className="header-actions">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search teachers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <i className="search-icon">🔍</i>
          </div>
          
          <button 
            onClick={fetchData} 
            style={{ padding: '0.5rem 1rem', marginLeft: '1rem', background: '#3182ce', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Refresh Data
          </button>
        </div>
      </div>
      
      <div className="teachers-table-container">
        <table className="modern-table">
          <thead>
            <tr>
              <th>Teacher</th>
              <th>Contact</th>
              <th>Location</th>
              <th>Specialization</th>
              <th>Experience</th>
              <th>Evaluations</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTeachers.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                  <div>
                    <p>No teachers found. Please check your database connection.</p>
                    <button 
                      onClick={fetchData} 
                      style={{ padding: '0.5rem 1rem', marginTop: '1rem', cursor: 'pointer' }}
                    >
                      Retry Loading
                    </button>
                  </div>
                </td>
              </tr>
            ) : filteredTeachers.map((teacher) => {
              // Find evaluations for this teacher, handling possible ID field differences
              const teacherEvaluations = evaluations.filter(e => 
                e.teacher_id === teacher.id || 
                e.teacher_id === teacher.id.toString()
              );
              
              // Handle potential key name differences between backend and frontend
              const teacherName = teacher.teacher_name || teacher.name;
              
              return (
                <tr key={teacher.id} className={!teacher.is_active ? 'inactive-row' : ''}>
                  <td>
                    <div className="teacher-cell">
                      <div className="teacher-info">
                        <strong>{teacherName}</strong>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-info-improved">
                      <div className="email">{teacher.email}</div>
                      <div className="divider"></div>
                      <div className="phone">{teacher.phone || teacher.mobile_number}</div>
                    </div>
                  </td>
                  <td>
                    <span>{teacher.state || teacher.school || 'N/A'}</span>
                  </td>
                  <td>
                    <div className="specialization-tags">
                      {Array.isArray(teacher.specialization) && teacher.specialization.length > 0 ? (
                        <>
                          {teacher.specialization.map((spec, index) => (
                            <span key={index} className="spec-tag">{spec}</span>
                          ))}
                        </>
                      ) : teacher.subject ? (
                        <span className="spec-tag">{teacher.subject}</span>
                      ) : (
                        <span className="spec-tag">Not specified</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="experience-badge">
                      {teacher.teaching_experience || teacher.experience_years || '0'} years
                    </span>
                  </td>
                  <td>
                    <div className="evaluation-stats">
                      <div className="eval-count">{teacherEvaluations.length} evaluations</div>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${teacher.is_active ? 'active' : 'inactive'}`}>
                      {teacher.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons" style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center' }}>
                      {teacher.is_active ? (
                        <button
                          className="action-btn disable"
                          onClick={() => handleTeacherAction(teacher.id, 'disable')}
                          title="Disable Teacher"
                          style={{
                            background: '#fff0f0',
                            border: 'none',
                            borderRadius: '8px',
                            width: 44,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 1px 4px 0 rgba(229,62,62,0.07)',
                            transition: 'background 0.2s',
                            cursor: 'pointer',
                            fontSize: 20,
                            position: 'relative',
                            minWidth: 44,
                            padding: 0
                          }}
                          onMouseOver={e => e.currentTarget.style.background = '#ffeaea'}
                          onMouseOut={e => e.currentTarget.style.background = '#fff0f0'}
                        >
                          <span role="img" aria-label="Disable" style={{ color: '#e53e3e', fontSize: 22 }}>🚫</span>
                        </button>
                      ) : (
                        <button
                          className="action-btn enable"
                          onClick={() => handleTeacherAction(teacher.id, 'enable')}
                          title="Enable Teacher"
                          style={{
                            background: '#e6fffa',
                            border: 'none',
                            borderRadius: '8px',
                            width: 44,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 1px 4px 0 rgba(56,178,172,0.07)',
                            transition: 'background 0.2s',
                            cursor: 'pointer',
                            fontSize: 20,
                            position: 'relative',
                            minWidth: 44,
                            padding: 0
                          }}
                          onMouseOver={e => e.currentTarget.style.background = '#b2f5ea'}
                          onMouseOut={e => e.currentTarget.style.background = '#e6fffa'}
                        >
                          <span role="img" aria-label="Enable" style={{ color: '#38b2ac', fontSize: 22 }}>✅</span>
                        </button>
                      )}
                      <button
                        className="action-btn change-password"
                        onClick={() => handleTeacherAction(teacher.id, 'change-password')}
                        title="Change Password"
                        style={{
                          background: '#f0f7ff',
                          border: 'none',
                          borderRadius: '8px',
                          width: 44,
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 4px 0 rgba(38,155,215,0.07)',
                          transition: 'background 0.2s',
                          cursor: 'pointer',
                          fontSize: 20,
                          position: 'relative',
                          minWidth: 44,
                          padding: 0
                        }}
                        onMouseOver={e => e.currentTarget.style.background = '#dbeafe'}
                        onMouseOut={e => e.currentTarget.style.background = '#f0f7ff'}
                      >
                        <span role="img" aria-label="Change Password" style={{ color: '#269bd7', fontSize: 22 }}>🔑</span>
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => handleTeacherAction(teacher.id, 'delete')}
                        title="Delete Teacher"
                        style={{
                          background: '#fff5f5',
                          border: 'none',
                          borderRadius: '8px',
                          width: 44,
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 4px 0 rgba(229,62,62,0.07)',
                          transition: 'background 0.2s',
                          cursor: 'pointer',
                          fontSize: 20,
                          position: 'relative',
                          minWidth: 44,
                          padding: 0
                        }}
                        onMouseOver={e => e.currentTarget.style.background = '#fed7d7'}
                        onMouseOut={e => e.currentTarget.style.background = '#fff5f5'}
                      >
                        <span role="img" aria-label="Delete" style={{ color: '#e53e3e', fontSize: 22 }}>🗑️</span>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const allSelected = filteredEvaluations.length > 0 && selectedEvaluations.length === filteredEvaluations.length;
  const someSelected = selectedEvaluations.length > 0 && selectedEvaluations.length < filteredEvaluations.length;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedEvaluations(filteredEvaluations.map(ev => ev.id));
    } else {
      setSelectedEvaluations([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedEvaluations(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const renderEvaluationsTab = () => (
    <div className="evaluations-content">
      <div className="section-header">
        <h3>Evaluation Management</h3>
        <div className="header-actions" style={{ display: 'flex', gap: 12 }}>
          <select 
            value={selectedClassLevel}
            onChange={e => setSelectedClassLevel(e.target.value)}
            className="filter-select"
            style={{ minWidth: 120 }}
          >
            <option value="">All Classes</option>
            {classLevels.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
          <select 
            value={selectedSubject} 
            onChange={(e) => setSelectedSubject(e.target.value)}
            className="filter-select"
          >
            <option value="">All Subjects</option>
            {subjects.map(subject => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>
          <select 
            value={selectedTeacher} 
            onChange={(e) => setSelectedTeacher(e.target.value)}
            className="filter-select"
          >
            <option value="">All Teachers</option>
            {teachers.filter(t => t.is_active).map(teacher => (
              <option key={teacher.id} value={teacher.id}>{teacher.teacher_name}</option>
            ))}
          </select>
        </div>
      </div>
      {/* Select All and Bulk Delete */}
      <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 1rem 0', gap: 16 }}>
        <input
          type="checkbox"
          checked={allSelected}
          ref={el => { if (el) el.indeterminate = someSelected; }}
          onChange={handleSelectAll}
          style={{ width: 18, height: 18, marginRight: 8 }}
          id="select-all-evals"
        />
        <label htmlFor="select-all-evals" style={{ fontWeight: 500, cursor: 'pointer' }}>Select All</label>
        {selectedEvaluations.length > 0 && (
          <button
            style={{ marginLeft: 16, background: '#e53e3e', color: 'white', border: 'none', borderRadius: 6, padding: '6px 18px', fontWeight: 600, cursor: 'pointer' }}
            onClick={() => {
              setActionType('delete');
              setSelectedEvaluationId(null);
              setShowConfirmModal(true);
            }}
          >
            Delete Selected ({selectedEvaluations.length})
          </button>
        )}
      </div>
      <div
        className="evaluations-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: (typeof window !== 'undefined' && window.innerWidth <= 576) ? '1fr' : '1fr 1fr',
          gap: (typeof window !== 'undefined' && window.innerWidth <= 576) ? 16 : 24,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {filteredEvaluations.map((evaluation) => {
          const teacher = teachers.find(t => t.id === evaluation.teacher_id);
          const totalMarks = typeof evaluation.total_marks === 'number' ? evaluation.total_marks : 0;
          const checked = selectedEvaluations.includes(evaluation.id);
          const isMobile = typeof window !== 'undefined' && window.innerWidth <= 576;
          return (
            <div
              key={evaluation.id}
              className="evaluation-card"
              style={{
                position: 'relative',
                ...(isMobile
                  ? {
                      margin: '0 0 16px 0',
                      width: '100%',
                      minWidth: 'unset',
                      padding: '12px 8px',
                      fontSize: '0.98em',
                    }
                  : {
                      margin: '12px',
                      maxWidth: 600,
                      minWidth: 420,
                      padding: '28px 32px',
                      fontSize: '1.12em',
                      boxSizing: 'border-box',
                    }),
              }}
            >
              {/* Header row for checkbox and delete icon */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? 8 : 12 }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => handleSelectOne(evaluation.id)}
                  style={{ width: 18, height: 18, marginRight: 8 }}
                  aria-label="Select Evaluation"
                />
                <button
                  className="delete-eval-btn"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, zIndex: 2 }}
                  title="Delete Evaluation"
                  onClick={() => {
                    setActionType('delete');
                    setSelectedEvaluationId(evaluation.id);
                    setShowConfirmModal(true);
                  }}
                  aria-label="Delete Evaluation"
                >
                  {/* Flat red trash SVG icon */}
                  <svg width="22" height="22" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 8.5V15.5M10 8.5V15.5M14 8.5V15.5M2.5 4.5H17.5M16.5 4.5V17.5C16.5 18.0523 16.0523 18.5 15.5 18.5H4.5C3.94772 18.5 3.5 18.0523 3.5 17.5V4.5M7.5 4.5V3.5C7.5 2.94772 7.94772 2.5 8.5 2.5H11.5C12.0523 2.5 12.5 2.94772 12.5 3.5V4.5" stroke="#e53e3e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              {/* Card content section below header */}
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: isMobile ? 6 : 12, marginTop: isMobile ? 8 : 16 }}>
                <div className="eval-meta" style={{ flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <div className="meta-item" style={{ fontSize: isMobile ? '0.88em' : '0.94em', display: 'flex', alignItems: 'center', gap: 2, padding: isMobile ? '2px 8px' : '3px 12px', background: '#f6f8fa', borderRadius: '12px' }}>
                    <i className="icon">📅</i>
                    <span>{new Date(evaluation.evaluation_date || evaluation.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="meta-divider" style={{ margin: '0 2px' }}>•</div>
                  <div className="meta-item" style={{ fontSize: isMobile ? '0.88em' : '0.94em', display: 'flex', alignItems: 'center', gap: 2, padding: isMobile ? '2px 8px' : '3px 12px', background: '#f6f8fa', borderRadius: '12px' }}>
                    <i className="icon">⏰</i>
                    <span>{new Date(evaluation.evaluation_date || evaluation.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                  </div>
                </div>
                <div className="score-badge" style={{ minWidth: isMobile ? 60 : 70, display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'flex-end', fontSize: isMobile ? '0.92em' : '0.98em', marginTop: isMobile ? 4 : 0 }}>
                  <span
                    className={`score-text ${totalMarks >= 32 ? 'excellent' : totalMarks >= 20 ? 'good' : 'needs-improvement'}`}
                    style={{ padding: isMobile ? '4px 12px' : '6px 16px', borderRadius: '8px', fontWeight: 600, fontSize: isMobile ? '0.98em' : '1em', minWidth: isMobile ? 60 : 70, textAlign: 'center', boxShadow: 'none', border: 'none', filter: 'none' }}
                  >
                    Score: {totalMarks}/40
                  </span>
                </div>
              </div>
              {/* Additional card data below date/score section */}
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 6 : 18, marginTop: isMobile ? 8 : 18 }}>
                {/* Modern pill design for Student/Teacher/Class/Subject */}
                <div style={{ flex: 1, minWidth: 80, marginRight: isMobile ? 0 : 8, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span style={{ fontWeight: 600, fontSize: isMobile ? '0.80em' : '0.86em', marginBottom: 2, letterSpacing: 0.2, textAlign: 'left', background: 'linear-gradient(123deg, rgba(38, 155, 215, 1) 15%, rgba(222, 4, 124, 1) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', color: 'transparent', display: 'inline-block' }}>Student:</span>
                  <span style={{ fontWeight: 400, fontSize: isMobile ? '0.98em' : '1em', color: '#222', padding: isMobile ? '2px 0' : '3px 0', width: '100%', textAlign: 'left', display: 'inline-block', marginBottom: 0, letterSpacing: 0.1, borderBottom: '1.5px solid #e2e8f0', background: 'none' }}>{evaluation.student_name || 'N/A'}</span>
                </div>
                <div style={{ flex: 1, minWidth: 80, marginRight: isMobile ? 0 : 8, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span style={{ fontWeight: 600, fontSize: isMobile ? '0.80em' : '0.86em', marginBottom: 2, letterSpacing: 0.2, textAlign: 'left', background: 'linear-gradient(123deg, rgba(38, 155, 215, 1) 15%, rgba(222, 4, 124, 1) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', color: 'transparent', display: 'inline-block' }}>Teacher:</span>
                  <span style={{ fontWeight: 400, fontSize: isMobile ? '0.98em' : '1em', color: '#222', padding: isMobile ? '2px 0' : '3px 0', width: '100%', textAlign: 'left', display: 'inline-block', marginBottom: 0, letterSpacing: 0.1, borderBottom: '1.5px solid #e2e8f0', background: 'none' }}>{teacher ? (teacher.teacher_name || teacher.name) : 'Unknown'}</span>
                </div>
                <div style={{ flex: 1, minWidth: 80, marginRight: isMobile ? 0 : 8, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span style={{ fontWeight: 600, fontSize: isMobile ? '0.80em' : '0.86em', marginBottom: 2, letterSpacing: 0.2, textAlign: 'left', background: 'linear-gradient(123deg, rgba(38, 155, 215, 1) 15%, rgba(222, 4, 124, 1) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', color: 'transparent', display: 'inline-block' }}>Class:</span>
                  <span style={{ fontWeight: 400, fontSize: isMobile ? '0.98em' : '1em', color: '#222', padding: isMobile ? '2px 0' : '3px 0', width: '100%', textAlign: 'left', display: 'inline-block', marginBottom: 0, letterSpacing: 0.1, borderBottom: '1.5px solid #e2e8f0', background: 'none' }}>{evaluation.class_level || 'N/A'}</span>
                </div>
                <div style={{ flex: 1, minWidth: 80, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span style={{ fontWeight: 600, fontSize: isMobile ? '0.80em' : '0.86em', marginBottom: 2, letterSpacing: 0.2, textAlign: 'left', background: 'linear-gradient(123deg, rgba(38, 155, 215, 1) 15%, rgba(222, 4, 124, 1) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', color: 'transparent', display: 'inline-block' }}>Subject:</span>
                  <span style={{ fontWeight: 400, fontSize: isMobile ? '0.98em' : '1em', color: '#222', padding: isMobile ? '2px 0' : '3px 0', width: '100%', textAlign: 'left', display: 'inline-block', marginBottom: 0, letterSpacing: 0.1, borderBottom: '1.5px solid #e2e8f0', background: 'none' }}>{(evaluation.subject || '').toUpperCase()}</span>
                </div>
              </div>
              {/* Notes Section */}
              {evaluation.notes && (
                <div style={{ background: '#f6f4fc', borderRadius: '14px', padding: isMobile ? '7px 12px' : '10px 18px', display: 'flex', alignItems: 'center', marginTop: isMobile ? 10 : 16 }}>
                  <span style={{ display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: isMobile ? '0.98em' : '1em', marginRight: 10, background: 'linear-gradient(123deg, rgba(38, 155, 215, 1) 15%, rgba(222, 4, 124, 1) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', color: 'transparent' }}>
                    <span style={{ marginRight: 4, display: 'inline-flex', alignItems: 'center' }}>
                      <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
                        <rect x="2" y="2" width="16" height="16" rx="4" fill="#e9e4f7" />
                        <path d="M6 13.5V15.5H8L14.5 9L12.5 7L6 13.5Z" fill="#de047c" stroke="#de047c" strokeWidth="1" />
                        <path d="M11.5 8L13.5 10" stroke="#269bd7" strokeWidth="1" strokeLinecap="round" />
                      </svg>
                    </span>
                    Notes:
                  </span>
                  <span style={{ fontWeight: 400, fontSize: isMobile ? '0.98em' : '1em', color: '#3a3a3a', marginLeft: 8 }}>{evaluation.notes}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <h1>CurioKAARDS Admin Dashboard</h1>
          <p>Teacher Management System</p>
        </div>
        <div className="header-right">
          <div className="admin-info">
            <span>Welcome, Admin</span>
          </div>
          <button 
            className="logout-btn"
            onClick={handleLogout}
          >
            <span>Logout</span>
            <i>→</i>
          </button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="dashboard-nav">
        <button 
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <i>📊</i> Overview
        </button>
        <button 
          className={`nav-tab ${activeTab === 'teachers' ? 'active' : ''}`}
          onClick={() => setActiveTab('teachers')}
        >
          <i>👥</i> Teachers
        </button>
        <button 
          className={`nav-tab ${activeTab === 'evaluations' ? 'active' : ''}`}
          onClick={() => setActiveTab('evaluations')}
        >
          <i>📝</i> Evaluations
        </button>
      </nav>

      {/* Main Content */}
      <main className="dashboard-main">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'teachers' && renderTeachersTab()}
        {activeTab === 'evaluations' && renderEvaluationsTab()}
      </main>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>Confirm Action</h3>
            </div>
            <div className="modal-body">
              {actionType === 'change-password' ? (
                <form autoComplete="off" onSubmit={e => { e.preventDefault(); }}>
                  <p>Enter a new password for this teacher:</p>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="admin-change-password"
                      autoComplete="new-password"
                      placeholder="New Password (6 digits)"
                      value={newPassword}
                      onChange={e => {
                        // Only allow digits, max 6
                        const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setNewPassword(digitsOnly);
                      }}
                      onKeyPress={e => {
                        if (!/[0-9]/.test(e.key) || newPassword.length >= 6) {
                          e.preventDefault();
                        }
                      }}
                      style={{ width: '100%', padding: '8px 38px 8px 8px', margin: '12px 0', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '1em' }}
                      autoFocus
                      maxLength={6}
                      pattern="\d{6}"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-5.05 0-9.27-3.11-10.94-7.5a10.05 10.05 0 0 1 1.73-2.82M1 1l22 22"/><path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5a3.5 3.5 0 0 0 2.47-5.97"/></svg>
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12C2.73 7.61 6.95 4.5 12 4.5s9.27 3.11 10.94 7.5c-1.67 4.39-5.89 7.5-10.94 7.5S2.73 16.39 1 12z"/><circle cx="12" cy="12" r="3.5"/></svg>
                      )}
                    </button>
                  </div>
                  <div style={{ fontSize: '0.98em', color: '#555', marginTop: 4, marginBottom: 2, lineHeight: 1.5 }}>
                    Password must be <strong>exactly 6 digits</strong> (numbers only).
                  </div>
                </form>
              ) : selectedEvaluationId || selectedEvaluations.length > 0 ? (
                <>
                  <p>Are you sure you want to delete {selectedEvaluationId ? 'this evaluation' : `these ${selectedEvaluations.length} evaluations`}?</p>
                  <p className="warning">This action will permanently remove {selectedEvaluationId ? 'the evaluation' : 'the selected evaluations'} from the system.</p>
                </>
              ) : (
                <>
                  <p>Are you sure you want to {actionType} this teacher?</p>
                  {actionType === 'delete' && (
                    <p className="warning">This action will permanently remove the teacher from the system.</p>
                  )}
                </>
              )}
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-cancel"
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedEvaluationId(null);
                  setSelectedEvaluations([]);
                  setNewPassword('');
                }}
              >
                Cancel
              </button>
              <button 
                className={`btn btn-confirm ${actionType}`}
                onClick={async () => {
                  if (actionType === 'change-password') {
                    const token = localStorage.getItem('adminToken');
                    try {
                      const res = await axios.put(`/api/admin/teachers/${selectedTeacherId}/password`, { newPassword }, { headers: { Authorization: `Bearer ${token}` } });
                      if (res.data.success) {
                        toast.success('Password changed successfully');
                        setShowConfirmModal(false);
                        setNewPassword('');
                        fetchData();
                      } else {
                        toast.error(res.data.message || 'Failed to change password');
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.message || 'Failed to change password');
                    }
                  } else {
                    confirmAction();
                  }
                }}
                disabled={actionType === 'change-password' && (!/^[0-9]{6}$/.test(newPassword))}
              >
                {actionType === 'change-password'
                  ? 'Change Password'
                  : actionType === 'enable'
                  ? 'Enable'
                  : actionType === 'disable'
                  ? 'Disable'
                  : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
